import { OrderDrawerResponse } from '../../hooks/useGetOrderDrawer';
import {
    Box,
    Text,
    VStack,
    HStack,
    Flex,
    Grid,
    Icon,
    Collapse,
    useDisclosure,
    Table,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    Tooltip,
    Tag,
    Checkbox,
    useToast,
} from '@chakra-ui/react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { TbArrowBackUp, TbChevronDown, TbChevronUp, TbCopy, TbTruck, TbX } from 'react-icons/tb';
import { formatPrice, formatPercentage } from '../../functions';
import { FullSupplierOrder, OrderedPart } from '../../hooks/useGetOrderDrawer';
import { SupplierNote } from '../../hooks/useGetOrderDetail';
import StatBox from '../StatBox';
import { formatMinGrossMarginThresholdSubtext } from '../GrossMarginSummaryValue';
import CopyButton from '../CopyButton';
import SupplierOrderInfoPopover from '../SupplierOrderInfoPopover';
import VendorSelector from '../VendorSelector';
import BulkActionBar, { BulkActionBarItem } from '../BulkActionBar';
import CancelReturnPartsModal, { CancelReturnPartSummary } from '../../modals/CancelReturnPartsModal';
import { Button, ConfirmDialog, Notice } from '../../ui';
import { getSupplierChangeSummary } from '../../utils/supplierChangeNotice';
import { useConfirmSupplierOrder } from '../../hooks/useConfirmSupplierOrder';
import { useAcceptSupplierChanges } from '../../hooks/useAcceptSupplierChanges';
import SessionContext from '../../contexts/SessionContext';

const isUnconfirmedInPartstrader = (order: FullSupplierOrder) => order.source === 'partstrader'
    && ((order.status === 'ordered' && !order.hasChanges) || !!order.awaitingPartstraderConfirmation);

const checkboxSx = {
    '&[data-checked]': {
        bg: '#0071E3',
        borderColor: '#0071E3',
    },
    '&[data-checked]:hover': {
        bg: '#005BC4',
        borderColor: '#005BC4',
    },
};

type SupplierOrderStatus = 'ordered' | 'confirmed' | 'rejected' | 'cancelled';

const getStatusColor = (status: SupplierOrderStatus) => {
    switch (status) {
        case 'confirmed':
            return 'purple';
        case 'ordered':
            return 'teal';
        case 'rejected':
            return 'gray';
        case 'cancelled':
            return 'red';
    }
};

const getStatusLabel = (status: SupplierOrderStatus) => {
    switch (status) {
        case 'confirmed':
            return 'Confirmed';
        case 'ordered':
            return 'Ordered';
        case 'rejected':
            return 'Rejected';
        case 'cancelled':
            return 'Cancelled';
    }
};

interface OrderedSectionProps {
    orders: FullSupplierOrder[];
    minGrossMarginThreshold?: number | null;
    supplierNotes?: Array<SupplierNote>;
    supplierMessageCounts?: Record<string, number>;
    onOpenSupplierCommunications?: (supplierOrderId: string, initialTab: 'quoting-comments' | 'order-messages') => void;
    selectedParts: Set<string>;
    onTogglePartSelection: (partId: string) => void;
    onToggleAllParts: (partIds: string[]) => void;
    refetch?: () => void;
    /** Deep-link from the Delivery Board: highlight this part's row. */
    clickedOrderPartId?: string;
}

const OrderedSectionCard = ({
    orders,
    minGrossMarginThreshold,
    supplierNotes = [],
    supplierMessageCounts = {},
    onOpenSupplierCommunications,
    selectedParts,
    onTogglePartSelection,
    onToggleAllParts,
    refetch,
    clickedOrderPartId,
}: OrderedSectionProps) => {
    return (
        <VStack spacing={4} align="stretch">
            {orders.map((order) => (
                <OrderedCardItem
                    key={order.supplierOrderId}
                    order={order}
                    minGrossMarginThreshold={minGrossMarginThreshold}
                    supplierNotes={supplierNotes}
                    supplierMessageCounts={supplierMessageCounts}
                    onOpenSupplierCommunications={onOpenSupplierCommunications}
                    selectedParts={selectedParts}
                    onTogglePartSelection={onTogglePartSelection}
                    onToggleAllParts={onToggleAllParts}
                    refetch={refetch}
                    clickedOrderPartId={clickedOrderPartId}
                />
            ))}
        </VStack>
    );
};

const OrderedCardItem = ({
    order,
    minGrossMarginThreshold,
    supplierNotes = [],
    supplierMessageCounts = {},
    onOpenSupplierCommunications,
    selectedParts,
    onTogglePartSelection,
    onToggleAllParts,
    refetch,
    clickedOrderPartId,
}: {
    order: FullSupplierOrder;
    minGrossMarginThreshold?: number | null;
    supplierNotes?: Array<SupplierNote>;
    supplierMessageCounts?: Record<string, number>;
    onOpenSupplierCommunications?: (supplierOrderId: string, initialTab: 'quoting-comments' | 'order-messages') => void;
    selectedParts: Set<string>;
    onTogglePartSelection: (partId: string) => void;
    onToggleAllParts: (partIds: string[]) => void;
    refetch?: () => void;
    clickedOrderPartId?: string;
}) => {
    const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });
    const toast = useToast();
    const acceptSupplierChanges = useAcceptSupplierChanges();
    const [rejectPartIds, setRejectPartIds] = useState<string[] | null>(null);
    const liveParts = useMemo(
        () => order.parts.filter((part) => !part.cancelled && !part.returned),
        [order.parts],
    );
    const changeSummary = useMemo(() => getSupplierChangeSummary({
        hasChanges: order.hasChanges,
        source: order.source,
        partstraderConfirmed: order.status === 'confirmed' && !order.awaitingPartstraderConfirmation,
        parts: liveParts.map((part) => ({
            id: part._id,
            buyPrice: part.buyPrice,
            correctedBuyPrice: part.correctedBuyPrice,
            listPrice: part.listPrice,
            correctedListPrice: part.correctedListPrice,
            partNumber: part.partNumber,
            correctedPartNumber: part.correctedPartNumber,
            eta: part.eta,
            correctedEta: part.correctedEta,
        })),
    }), [liveParts, order.hasChanges, order.source, order.status, order.awaitingPartstraderConfirmation]);

    const changeNotice = changeSummary.notice;
    const showChangeActions = changeSummary.actionablePartIds.length > 0;
    const rejectWholeOrder = isUnconfirmedInPartstrader(order);
    const rejectPartIdScope = rejectWholeOrder
        ? liveParts.map((part) => part._id)
        : changeSummary.actionablePartIds;
    const supplierCommunicationCount = useMemo(() => {
        const notesForSupplier = supplierNotes.filter((note) => note.supplierName === order.title).length;
        const messagesForSupplier = supplierMessageCounts[order.supplierOrderId] ?? 0;
        return notesForSupplier + messagesForSupplier;
    }, [order.supplierOrderId, order.title, supplierMessageCounts, supplierNotes]);
    const supplierCommunicationInitialTab = (
        order.source === 'opstrax' || (supplierMessageCounts[order.supplierOrderId] ?? 0) > 0
    )
        ? 'order-messages'
        : 'quoting-comments';
    const shouldShowSupplierCommunicationsIcon = (
        (order.source === 'partstrader' || order.source === 'opstrax' || order.source === 'ccc') &&
        ['ordered', 'confirmed'].includes(order.status)
    );

    const sessionContext = useContext(SessionContext);
    const isConfirmedOrder = order.status === 'confirmed';
    // Also select on a still-Ordered PO so the bulk bar can offer Mark Confirmed.
    const canBulkConfirm = !!order.canManualConfirm;
    // Parts that can't be cancelled or returned yet (OpsTrax before confirmation)
    // are still selectable when the only action is Mark Confirmed.
    const canMarkConfirmed = canBulkConfirm && !!sessionContext?.permissions?.includes('place-order');
    const isPartSelectable = (part: OrderedPart) => (
        (part.canReturn && (isConfirmedOrder || canBulkConfirm))
        || (canMarkConfirmed && !part.cancelled && !part.returned)
    );
    const selectableParts = order.parts.filter(isPartSelectable);
    const allSelectableSelected = selectableParts.length > 0 && selectableParts.every((part) => selectedParts.has(part._id));

    return (
        <>
        <Box
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="lg"
            overflow="hidden"
            bg="white"
            boxShadow="none"
        >
            <Flex px={4} py={3} bg="white" borderBottomWidth="1px" borderColor="gray.200" justify="space-between" align="center">
                <Text fontSize="md" fontWeight="semibold" color="gray.800" noOfLines={1}>
                    {order.title}
                </Text>
                <HStack spacing={3}>
                    {shouldShowSupplierCommunicationsIcon && (
                        <Tooltip label="Supplier Communications" placement="top">
                            <Box position="relative">
                                <IconButton
                                    aria-label={`Open supplier communications for ${order.title}`}
                                    icon={<Icon as={TbTruck as React.ElementType} boxSize="18px" strokeWidth={2.25} />}
                                    boxSize="24px"
                                    minW="24px"
                                    h="24px"
                                    variant="ghost"
                                    color="orange.500"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onOpenSupplierCommunications?.(order.supplierOrderId, supplierCommunicationInitialTab);
                                    }}
                                    isDisabled={!onOpenSupplierCommunications}
                                />
                                {supplierCommunicationCount > 0 && (
                                    <Flex
                                        position="absolute"
                                        top="-4px"
                                        right="-4px"
                                        minW="14px"
                                        h="14px"
                                        px="3px"
                                        borderRadius="full"
                                        bg="orange.500"
                                        color="white"
                                        align="center"
                                        justify="center"
                                        fontSize="9px"
                                        fontWeight="bold"
                                        lineHeight="1"
                                        pointerEvents="none"
                                    >
                                        {supplierCommunicationCount}
                                    </Flex>
                                )}
                            </Box>
                        </Tooltip>
                    )}
                    <HStack spacing={2}>
                        {order.status && getStatusLabel(order.status as SupplierOrderStatus) && (
                            <>
                                <Tag
                                    size="sm"
                                    colorScheme={getStatusColor(order.status as SupplierOrderStatus)}
                                    variant="subtle"
                                    borderRadius="md"
                                    fontWeight="medium"
                                    fontSize="xs"
                                >
                                    {getStatusLabel(order.status as SupplierOrderStatus)}
                                </Tag>
                                <Text fontSize="sm" color="gray.600" fontWeight="semibold">•</Text>
                            </>
                        )}
                        <Text fontSize="sm" color="gray.600" fontWeight='semibold'>
                            {new Date(order.timePlaced).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} • {new Date(order.timePlaced).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </Text>
                    </HStack>
                    <SupplierOrderInfoPopover
                        label="Order Info"
                        displayId={order.displayId}
                        phone={order.supplierPhone}
                        email={order.supplierEmail}
                    />
                    <IconButton
                        aria-label={isOpen ? `Collapse ${order.title}` : `Expand ${order.title}`}
                        icon={<Icon as={(isOpen ? TbChevronUp : TbChevronDown) as React.ElementType} boxSize={4} />}
                        size="sm"
                        variant="ghost"
                        color="gray.600"
                        onClick={onToggle}
                    />
                </HStack>
            </Flex>

            <Collapse
                in={isOpen}
                animateOpacity
                transition={{ enter: { duration: 0.35 }, exit: { duration: 0.25 } }}
            >
                <Box p={4}>
                    <Grid templateColumns="repeat(4, 1fr)" gap={4}>
                        <StatBox label="TOTAL PARTS" value={order.totalParts} />
                        <StatBox label="ETA" value={order.eta} />
                        <StatBox label="ORDER VALUE" value={formatPrice(order.orderValue)} />
                        <StatBox
                            label="GROSS MARGIN"
                            value={formatPercentage(order.grossMargin)}
                            subValue={formatMinGrossMarginThresholdSubtext(minGrossMarginThreshold)}
                        />
                    </Grid>
                </Box>
                {changeNotice && (
                    <Box mb={4} px={4}>
                        <Notice
                            tone={changeNotice.tone}
                            title={changeNotice.title}
                            action={showChangeActions && (
                                <HStack spacing={2} pl={4} flexShrink={0}>
                                    <Button
                                        size="xs"
                                        variant="outline"
                                        onClick={() => setRejectPartIds(rejectPartIdScope)}
                                        isDisabled={rejectPartIdScope.length === 0 || acceptSupplierChanges.isPending}
                                    >
                                        Reject Change
                                    </Button>
                                    <Button
                                        size="xs"
                                        variant="solid"
                                        onClick={() => {
                                            acceptSupplierChanges.mutate({ supplierOrderId: order.supplierOrderId }, {
                                                onSuccess: () => {
                                                    toast({
                                                        title: 'Accepting supplier changes',
                                                        description: 'The changes are being applied. Please check back shortly.',
                                                        status: 'success',
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });
                                                    refetch?.();
                                                },
                                                onError: () => {
                                                    toast({
                                                        title: 'Failed to accept supplier changes',
                                                        status: 'error',
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });
                                                },
                                            });
                                        }}
                                        isLoading={acceptSupplierChanges.isPending}
                                        loadingText="Accepting..."
                                    >
                                        Accept Change
                                    </Button>
                                </HStack>
                            )}
                        >
                            {changeNotice.body}
                        </Notice>
                    </Box>
                )}
                <Box borderTop="1px solid" borderColor="gray.200">
                    <Table variant="simple" size="sm" w="100%">
                        <Thead bg="gray.50">
                            <Tr>
                                <Th pl={4} py={3} borderColor="gray.200" color="gray.500" fontSize="xs" w="40px">
                                    <Checkbox
                                        isChecked={allSelectableSelected}
                                        onChange={() => onToggleAllParts(selectableParts.map((part) => part._id))}
                                        isDisabled={selectableParts.length === 0}
                                        size="md"
                                        sx={checkboxSx}
                                    />
                                </Th>
                                <Th pl={2} py={3} borderColor="gray.200" color="gray.500" fontSize="xs">
                                    Part Name &amp; Number
                                </Th>
                                <Th isNumeric py={3} borderColor="gray.200" color="gray.500" fontSize="xs">
                                    ETA
                                </Th>
                                <Th isNumeric py={3} borderColor="gray.200" color="gray.500" fontSize="xs">
                                    List Price
                                </Th>
                                <Th isNumeric pr={6} py={3} borderColor="gray.200" color="gray.500" fontSize="xs">
                                    Gross Margin
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {order.parts.map((part, index) => {
                                const inactive = !!part.cancelled || !!part.returned;
                                const rowColor = part.cancelled ? 'red' : part.returned ? 'blue' : null;
                                const textColor = rowColor ? `${rowColor}.500` : 'gray.900';
                                const isClickedPart = !!clickedOrderPartId && part._id === clickedOrderPartId;

                                const hasPnChange = !inactive && !!part.correctedPartNumber && part.correctedPartNumber !== part.partNumber;
                                const hasEtaChange = !inactive && !!part.correctedEta && part.correctedEta !== part.eta;
                                const hasBuyChange = !inactive && part.correctedBuyPrice != null && part.correctedBuyPrice !== part.buyPrice;
                                const hasListChange = !inactive && part.correctedListPrice != null && part.correctedListPrice !== part.listPrice;
                                const effectiveBuy = part.correctedBuyPrice ?? part.buyPrice;
                                const effectiveList = part.correctedListPrice ?? part.listPrice;
                                const originalMargin = part.listPrice && part.buyPrice ? (part.listPrice - part.buyPrice) / part.listPrice : null;
                                const correctedMargin = (hasBuyChange || hasListChange) && effectiveList ? (effectiveList - effectiveBuy) / effectiveList : null;

                                return (
                                <Tr
                                    key={part._id}
                                    p={4}
                                    _hover={{ bg: rowColor ? `${rowColor}.50` : "gray.25" }}
                                    bg={rowColor ? `${rowColor}.50` : isClickedPart ? 'blue.50' : undefined}
                                    opacity={inactive ? 0.6 : 1}
                                >
                                    <Td pl={4} py={3} borderColor="gray.200">
                                        <Checkbox
                                            isChecked={selectedParts.has(part._id)}
                                            onChange={() => onTogglePartSelection(part._id)}
                                            isDisabled={!isPartSelectable(part)}
                                            size="md"
                                            sx={checkboxSx}
                                        />
                                    </Td>
                                    <Td pl={2} py={3} borderColor="gray.200">
                                        <VStack align="start" spacing={0}>
                                            <HStack spacing={1.5}>
                                                <Text fontWeight="medium" color={textColor} textDecoration={inactive ? 'line-through' : undefined}>
                                                    {part.title}
                                                </Text>
                                                {part.returned && (
                                                    <Tooltip label="Returned" hasArrow placement="top">
                                                        <Box display="inline-flex">
                                                            <Icon as={TbArrowBackUp as React.ElementType} w={3.5} h={3.5} color="blue.400" />
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                            </HStack>
                                            {hasPnChange ? (
                                                <VStack align="start" spacing={0}>
                                                    <Text fontSize="xs" color="gray.500" textDecoration="line-through">
                                                        {part.partNumber || '—'}
                                                    </Text>
                                                    <CopyButton
                                                        text={part.correctedPartNumber as string}
                                                        label="Copy part number"
                                                        iconAs={TbCopy as unknown as React.ComponentProps<typeof CopyButton>['iconAs']}
                                                        tooltipAnchor={
                                                            <Text fontSize="xs" color="orange.500" fontWeight="medium">
                                                                {part.correctedPartNumber}
                                                            </Text>
                                                        }
                                                        minW="auto"
                                                        h="auto"
                                                        p={0.5}
                                                    />
                                                </VStack>
                                            ) : part.partNumber ? (
                                                <CopyButton
                                                    text={part.partNumber}
                                                    label="Copy part number"
                                                   
                                                    iconAs={TbCopy as unknown as React.ComponentProps<typeof CopyButton>['iconAs']}
                                                    tooltipAnchor={
                                                        <Text
                                                            fontSize="xs"
                                                            color={inactive ? textColor : 'gray.500'}
                                                            textDecoration={inactive ? 'line-through' : undefined}
                                                        >
                                                            {part.partNumber}
                                                        </Text>
                                                    }
                                                    minW="auto"
                                                    h="auto"
                                                    p={0.5}
                                                />
                                            ) : null}
                                        </VStack>
                                    </Td>
                                    <Td
                                        isNumeric
                                        py={3}
                                        fontWeight="medium"
                                        color={textColor}
                                        borderColor="gray.200"
                                        textDecoration={hasEtaChange ? undefined : (inactive ? 'line-through' : undefined)}
                                    >
                                        {hasEtaChange ? (
                                            <VStack align="flex-end" spacing={0}>
                                                <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                                                    {part.eta || '-'}
                                                </Text>
                                                <Text fontSize="sm" color="orange.500" fontWeight="medium">
                                                    {part.correctedEta}
                                                </Text>
                                            </VStack>
                                        ) : (
                                            part.eta || '-'
                                        )}
                                    </Td>
                                    <Td
                                        isNumeric
                                        py={3}
                                        fontWeight="medium"
                                        color={textColor}
                                        borderColor="gray.200"
                                        textDecoration={hasListChange ? undefined : (inactive ? 'line-through' : undefined)}
                                    >
                                        {hasListChange ? (
                                            <VStack align="flex-end" spacing={0}>
                                                <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                                                    {part.listPrice ? formatPrice(part.listPrice) : '-'}
                                                </Text>
                                                <Text fontSize="sm" color="orange.500" fontWeight="medium">
                                                    {formatPrice(part.correctedListPrice as number)}
                                                </Text>
                                            </VStack>
                                        ) : (
                                            part.listPrice ? formatPrice(part.listPrice) : '-'
                                        )}
                                    </Td>
                                    <Td
                                        isNumeric
                                        pr={6}
                                        py={3}
                                        fontWeight="medium"
                                        color={textColor}
                                        borderColor="gray.200"
                                        textDecoration={(hasBuyChange || hasListChange) ? undefined : (inactive ? 'line-through' : undefined)}
                                    >
                                        {(hasBuyChange || hasListChange) && correctedMargin !== null ? (
                                            <VStack align="flex-end" spacing={0}>
                                                <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                                                    {originalMargin !== null ? formatPercentage(originalMargin) : '-'}
                                                </Text>
                                                <Text fontSize="sm" color="orange.500" fontWeight="medium">
                                                    {formatPercentage(correctedMargin)}
                                                </Text>
                                            </VStack>
                                        ) : (
                                            originalMargin !== null ? formatPercentage(originalMargin) : '-'
                                        )}
                                    </Td>
                                </Tr>
                            );})}
                        </Tbody>
                    </Table>
                </Box>
            </Collapse>
        </Box>
        <CancelReturnPartsModal
            isOpen={rejectPartIds !== null}
            onClose={() => setRejectPartIds(null)}
            supplierOrderId={order.supplierOrderId}
            source="partstrader"
            cancelOnly={rejectWholeOrder}
            selectedPartIds={rejectPartIds ?? []}
            parts={(rejectPartIds ?? [])
                .map((partId) => order.parts.find((part) => part._id === partId))
                .filter((part): part is NonNullable<typeof part> => !!part)
                .map((part) => ({
                    partId: part._id,
                    name: part.title,
                    partNumber: part.correctedPartNumber ?? part.partNumber,
                }))}
            onSuccess={() => {
                refetch?.();
                setRejectPartIds(null);
            }}
        />
        </>
    );
};


interface OrderedTabPanelProps {
    data: NonNullable<OrderDrawerResponse['ordered']>;
    supplierNotes?: Array<SupplierNote>;
    supplierMessageCounts?: Record<string, number>;
    minGrossMarginThreshold?: number | null;
    onOpenSupplierCommunications?: (supplierOrderId: string, initialTab: 'quoting-comments' | 'order-messages') => void;
    refetch?: () => void;
    /** Deep-link from the Delivery Board: open on this supplier order's tab and highlight this part. */
    clickedSupplierOrderId?: string;
    clickedOrderPartId?: string;
}

interface OrderedDisplayTab {
    key: 'ordered' | 'confirmed' | 'rejected' | 'cancel-return' | 'all';
    title: string;
    orders: FullSupplierOrder[];
    total: number;
}

const filterOrdersByVendor = (
    orders: FullSupplierOrder[],
    selectedVendors: Array<string> | 'all',
): FullSupplierOrder[] => {
    if (selectedVendors === 'all') return orders;

    const vendorFilter = new Set(selectedVendors);
    return orders.filter((order) => vendorFilter.has(order.title.trim()));
};

const OrderedTabPanel = ({
    data,
    supplierNotes = [],
    supplierMessageCounts = {},
    minGrossMarginThreshold,
    onOpenSupplierCommunications,
    refetch,
    clickedSupplierOrderId,
    clickedOrderPartId,
}: OrderedTabPanelProps) => {
    const [tabIndex, setTabIndex] = useState(0);
    const [selectedVendors, setSelectedVendors] = useState<Array<string> | 'all'>('all');
    const [selectedParts, setSelectedParts] = useState<Set<string>>(() => new Set());
    const [bulkCancelModal, setBulkCancelModal] = useState<{
        supplierOrderId: string;
        source?: FullSupplierOrder['source'];
        partIds: string[];
        parts: CancelReturnPartSummary[];
        action: 'return' | 'cancellation';
        cancelOnly: boolean;
    } | null>(null);
    const [markConfirmTarget, setMarkConfirmTarget] = useState<{
        supplierOrderId: string;
        title: string;
    } | null>(null);
    const sessionContext = useContext(SessionContext);
    const toast = useToast();
    const confirmSupplierOrder = useConfirmSupplierOrder();

    const displayTabs = useMemo<OrderedDisplayTab[]>(() => {
        const orderedOrders = data.ordered?.orders ?? [];
        const confirmedOrders = data.confirmed?.orders ?? [];
        const rejectedOrders = data.rejected?.orders ?? [];
        const allOrderMap = new Map<string, FullSupplierOrder>();
        const allBuckets = [
            ...orderedOrders,
            ...confirmedOrders,
            ...(data.cancelled?.orders ?? []),
            ...rejectedOrders,
        ];
        allBuckets.forEach((order) => {
            const key = order.supplierOrderId || `${order.title}-${order.timePlaced}`;
            if (!allOrderMap.has(key)) {
                allOrderMap.set(key, order);
            }
        });
        const allOrders = Array.from(allOrderMap.values());

        const cancelReturnOrders = allOrders
            .map((order) => {
                const parts = order.parts.filter((part) => (
                    !!part.returned ||
                    !!part.cancelled ||
                    order.status === 'cancelled'
                ));
                if (parts.length === 0) return null;

                const totalList = parts.reduce((sum, part) => sum + (part.listPrice ?? 0), 0);
                const totalBuy = parts.reduce((sum, part) => sum + (part.buyPrice ?? 0), 0);

                return {
                    ...order,
                    parts,
                    totalParts: parts.length,
                    orderValue: totalList,
                    grossMargin: totalList > 0 ? (totalList - totalBuy) / totalList : 0,
                } satisfies FullSupplierOrder;
            })
            .filter((order): order is FullSupplierOrder => order !== null);

        const tabs: OrderedDisplayTab[] = [];
        if (orderedOrders.length > 0) {
            tabs.push({
                key: 'ordered',
                title: 'Ordered',
                orders: orderedOrders,
                total: orderedOrders.reduce((sum, order) => sum + order.totalParts, 0),
            });
        }
        if (confirmedOrders.length > 0) {
            tabs.push({
                key: 'confirmed',
                title: 'Confirmed',
                orders: confirmedOrders,
                total: confirmedOrders.reduce((sum, order) => sum + order.totalParts, 0),
            });
        }
        if (rejectedOrders.length > 0) {
            tabs.push({
                key: 'rejected',
                title: 'Rejected',
                orders: rejectedOrders,
                total: rejectedOrders.reduce((sum, order) => sum + order.totalParts, 0),
            });
        }
        if (cancelReturnOrders.length > 0) {
            tabs.push({
                key: 'cancel-return',
                title: 'Cancel / Return',
                orders: cancelReturnOrders,
                total: cancelReturnOrders.reduce((sum, order) => sum + order.totalParts, 0),
            });
        }
        if (allOrders.length > 0) {
            tabs.push({
                key: 'all',
                title: 'All',
                orders: allOrders,
                total: allOrders.reduce((sum, order) => sum + order.totalParts, 0),
            });
        }

        return tabs;
    }, [data]);

    useEffect(() => {
        // Deep-link: land on the tab containing the clicked supplier order.
        const clickedIndex = clickedSupplierOrderId
            ? displayTabs.findIndex((tab) =>
                tab.key !== 'all'
                && tab.orders.some((order) => order.supplierOrderId === clickedSupplierOrderId))
            : -1;
        setTabIndex(clickedIndex >= 0 ? clickedIndex : 0);
        setSelectedVendors('all');
        setSelectedParts(new Set());
    }, [data, displayTabs, clickedSupplierOrderId]);

    useEffect(() => {
        setSelectedVendors('all');
        setSelectedParts(new Set());
    }, [tabIndex]);

    const togglePartSelection = (partId: string) => {
        setSelectedParts((prev) => {
            const next = new Set(prev);
            if (next.has(partId)) {
                next.delete(partId);
            } else {
                next.add(partId);
            }
            return next;
        });
    };

    const toggleAllPartsInSection = (partIds: string[]) => {
        if (partIds.length === 0) return;

        setSelectedParts((prev) => {
            const next = new Set(prev);
            const allSelected = partIds.every((id) => next.has(id));
            if (allSelected) {
                partIds.forEach((id) => next.delete(id));
            } else {
                partIds.forEach((id) => next.add(id));
            }
            return next;
        });
    };

    const activeTab = displayTabs[tabIndex] ?? displayTabs[0];

    const vendorOptions = useMemo(() => (
        Array.from(new Set(
            (activeTab?.orders ?? [])
                .map((order) => order.title.trim())
                .filter(Boolean),
        )).sort((a, b) => a.localeCompare(b))
    ), [activeTab?.orders]);

    const normalizedSelectedVendors = useMemo(() => {
        if (selectedVendors === 'all') return 'all';
        return selectedVendors.filter((vendor) => vendorOptions.includes(vendor));
    }, [selectedVendors, vendorOptions]);

    const partOrderIndex = useMemo(() => {
        const index = new Map<string, FullSupplierOrder>();
        displayTabs.forEach((tab) => {
            tab.orders.forEach((order) => {
                order.parts.forEach((part) => {
                    if (!index.has(part._id)) index.set(part._id, order);
                });
            });
        });
        return index;
    }, [displayTabs]);

    const selectedPartEntries = useMemo(() => (
        Array.from(selectedParts).flatMap((partId) => {
            const order = partOrderIndex.get(partId);
            if (!order) return [];
            const part = order.parts.find((p) => p._id === partId);
            if (!part) return [];
            return [{ partId, part, order }];
        })
    ), [selectedParts, partOrderIndex]);

    const singleSupplierSelected =
        new Set(selectedPartEntries.map(({ order }) => order.supplierOrderId)).size === 1;
    const allSelectedReturnable =
        selectedPartEntries.length > 0
        && selectedPartEntries.every(({ part }) => !!part.canReturn);

    // canManualConfirm is server-computed; place-order mirrors the API's gate.
    const selectedConfirmOrder = singleSupplierSelected && selectedPartEntries.length > 0
        ? selectedPartEntries[0].order
        : null;
    const canMarkConfirmedSelection =
        !!selectedConfirmOrder
        && !!selectedConfirmOrder.canManualConfirm
        && !!sessionContext?.permissions?.includes('place-order');

    const selectedOrder = singleSupplierSelected ? selectedPartEntries[0]?.order : undefined;
    const returnBlocked = !!selectedOrder && isUnconfirmedInPartstrader(selectedOrder);

    const bulkBarActions = useMemo((): BulkActionBarItem[] => {
        const actions: BulkActionBarItem[] = [];
        if (allSelectedReturnable && singleSupplierSelected) {
            actions.push({
                id: 'cancel',
                label: 'Cancel',
                icon: TbX as React.ElementType,
                danger: true,
            });
            if (!returnBlocked) {
                actions.push({
                    id: 'return',
                    label: 'Return',
                    icon: TbArrowBackUp as React.ElementType,
                });
            }
        }
        if (canMarkConfirmedSelection) {
            actions.push({
                id: 'confirm',
                label: 'Mark Confirmed',
                iconName: 'checkCircle',
                success: true,
            });
        }
        return actions;
    }, [allSelectedReturnable, singleSupplierSelected, returnBlocked, canMarkConfirmedSelection]);

    if (displayTabs.length === 0) {
        return null;
    }

    const clearSelection = () => setSelectedParts(new Set());

    const handleMarkConfirmed = () => {
        if (!markConfirmTarget) return;
        confirmSupplierOrder.mutate({ supplierOrderId: markConfirmTarget.supplierOrderId }, {
            onSuccess: () => {
                toast({
                    title: 'Order marked Confirmed',
                    description: `${markConfirmTarget.title} — parts moved to Confirmed.`,
                    status: 'success',
                });
                setMarkConfirmTarget(null);
                clearSelection();
                refetch?.();
            },
            onError: () => {
                toast({
                    title: 'Could not mark order Confirmed',
                    description: 'Something went wrong. Please try again.',
                    status: 'error',
                });
            },
        });
    };

    const handleBulkAction = (actionId: string) => {
        if (actionId === 'confirm') {
            if (!canMarkConfirmedSelection || !selectedConfirmOrder) return;
            setMarkConfirmTarget({
                supplierOrderId: selectedConfirmOrder.supplierOrderId,
                title: selectedConfirmOrder.title,
            });
            return;
        }
        if (!allSelectedReturnable || !singleSupplierSelected || selectedPartEntries.length === 0) {
            return;
        }
        const order = selectedPartEntries[0].order;
        const unconfirmedPartstrader = isUnconfirmedInPartstrader(order);
        const livePartIds = order.parts.filter((p) => !p.cancelled && !p.returned).map((p) => p._id);
        if (unconfirmedPartstrader && selectedPartEntries.length < livePartIds.length) {
            setSelectedParts((prev) => new Set(Array.from(prev).concat(livePartIds)));
            toast({
                title: `${order.title} hasn't confirmed this order in PartsTrader yet`,
                description: `PartsTrader can only cancel the whole order until they do, so all ${livePartIds.length} parts are now selected.`,
                status: 'warning',
                duration: 6000,
                isClosable: true,
            });
            return;
        }
        setBulkCancelModal({
            supplierOrderId: order.supplierOrderId,
            source: order.source,
            partIds: selectedPartEntries.map(({ partId }) => partId),
            parts: selectedPartEntries.map(({ part }) => ({
                partId: part._id,
                name: part.title,
                partNumber: part.correctedPartNumber ?? part.partNumber,
            })),
            action: actionId === 'return' ? 'return' : 'cancellation',
            cancelOnly: unconfirmedPartstrader,
        });
    };

    return (
        <Box>
        <Tabs
            variant="unstyled"
            colorScheme="blue"
            index={tabIndex}
            onChange={setTabIndex}
        >
            <Flex justify="space-between" align="flex-end" mb={4} borderBottom="1.5px solid" borderColor="gray.200">
                <TabList border="none" gap={0}>
                    {displayTabs.map((tab) => (
                        <Tab
                            key={tab.key}
                        fontWeight="medium"
                        fontSize="sm"
                        color="gray.500"
                        bg="transparent"
                        border="none"
                        borderBottom="1.5px solid"
                        borderColor="transparent"
                        mb="-1.5px"
                        py={2}
                        px={3}
                        _selected={{
                            color: "gray.800",
                            borderColor: "gray.500",
                        }}
                        _hover={{
                            color: "gray.700",
                        }}
                    >
                        <HStack spacing={0}>
                            <Text>{tab.title}</Text>
                            {tab.total > 0 && (
                                <Flex
                                    ml={1.5}
                                    bg="gray.100"
                                    borderRadius="lg"
                                    h="18px"
                                    minW="18px"
                                    px="5px"
                                    align="center"
                                    justify="center"
                                    flexShrink={0}
                                >
                                    <Text
                                        fontSize="9px"
                                        fontWeight="bold"
                                        lineHeight="1"
                                        color="gray.800"
                                    >
                                        {tab.total}
                                    </Text>
                                </Flex>
                            )}
                        </HStack>
                        </Tab>
                    ))}
                </TabList>
                <Box pb={2} flexShrink={0}>
                    <VendorSelector
                        vendors={vendorOptions}
                        selectedVendors={normalizedSelectedVendors}
                        onVendorChange={setSelectedVendors}
                        width="240px"
                    />
                </Box>
            </Flex>

            <TabPanels>
                {displayTabs.map((tab) => (
                    <TabPanel key={tab.key} px={0} py={0}>
                        <OrderedSectionCard
                            orders={filterOrdersByVendor(tab.orders, normalizedSelectedVendors)}
                            minGrossMarginThreshold={minGrossMarginThreshold}
                            supplierNotes={supplierNotes}
                            supplierMessageCounts={supplierMessageCounts}
                            onOpenSupplierCommunications={onOpenSupplierCommunications}
                            selectedParts={selectedParts}
                            onTogglePartSelection={togglePartSelection}
                            onToggleAllParts={toggleAllPartsInSection}
                            refetch={refetch}
                            clickedOrderPartId={clickedOrderPartId}
                        />
                    </TabPanel>
                ))}
            </TabPanels>
        </Tabs>

            <BulkActionBar
                selectedCount={selectedParts.size}
                actions={bulkBarActions}
                onClearSelection={clearSelection}
                onAction={handleBulkAction}
                noActionsMessage="No shared actions available. Try selecting parts from only one supplier."
            />

            <CancelReturnPartsModal
                isOpen={bulkCancelModal !== null}
                onClose={() => setBulkCancelModal(null)}
                supplierOrderId={bulkCancelModal?.supplierOrderId ?? ''}
                source={bulkCancelModal?.source}
                selectedPartIds={bulkCancelModal?.partIds ?? []}
                parts={bulkCancelModal?.parts ?? []}
                initialAction={bulkCancelModal?.action ?? 'cancellation'}
                cancelOnly={bulkCancelModal?.cancelOnly}
                onSuccess={() => {
                    clearSelection();
                    refetch?.();
                    setBulkCancelModal(null);
                }}
            />

            <ConfirmDialog
                isOpen={markConfirmTarget !== null}
                onClose={() => setMarkConfirmTarget(null)}
                onConfirm={handleMarkConfirmed}
                title="Mark order confirmed?"
                confirmLabel="Mark Confirmed"
                confirmColorScheme="green"
                isConfirmLoading={confirmSupplierOrder.isPending}
            >
                {markConfirmTarget?.title} never confirmed this order through the
                marketplace. This marks the order and its active parts as Confirmed — use it
                when the vendor confirmed another way (e.g. over the phone). Re-keying may
                start once every part on the RO is confirmed.
            </ConfirmDialog>
        </Box>
    );
};

export default OrderedTabPanel;

